import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { useState, useCallback } from 'react';
// @mui
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
//
import Iconify from '../iconify';
import Image from '../image';
import ImageCropperDialog from '../image-cropper/ImageCropperDialog';
//
import RejectionFiles from './errors-rejection-files';

// ----------------------------------------------------------------------

export default function UploadAvatar({ error, file, disabled, helperText, sx, crop, onDrop, aspectRatio = 1, circularCrop = true, lockAspectRatio = false, ...other }) {
  const [openCropper, setOpenCropper] = useState(false);
  const [cropImage, setCropImage] = useState(null);

  const handleDrop = useCallback(
    (acceptedFiles) => {
      const newFile = acceptedFiles[0];
      if (newFile && crop) {
        setCropImage(URL.createObjectURL(newFile));
        setOpenCropper(true);
      } else if (onDrop) {
        onDrop(acceptedFiles);
      }
    },
    [crop, onDrop]
  );

  const handleSaveCroppedImage = useCallback(
    (croppedBlob) => {
      // Create a file from the blob with a name
      const croppedFile = new File([croppedBlob], "cropped-image.png", { type: "image/png" });
      if (onDrop) {
        onDrop([Object.assign(croppedFile, {
          preview: URL.createObjectURL(croppedFile),
        })]);
      }
    },
    [onDrop]
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject, fileRejections } = useDropzone({
    multiple: false,
    disabled,
    accept: {
      'image/*': [],
    },
    onDrop: handleDrop,
    ...other,
  });

  const hasFile = !!file;

  const hasError = isDragReject || !!error;

  const imgUrl = typeof file === 'string' ? file : file?.preview;

  const renderPreview = hasFile && (
    <Image
      alt="avatar"
      src={imgUrl}
      sx={{
        width: 1,
        height: 1,
        borderRadius: circularCrop ? '50%' : 1,
      }}
    />
  );

  const renderPlaceholder = (
    <Stack
      alignItems="center"
      justifyContent="center"
      spacing={1}
      className="upload-placeholder"
      sx={{
        top: 0,
        left: 0,
        width: 1,
        height: 1,
        zIndex: 9,
        borderRadius: circularCrop ? '50%' : 1,
        position: 'absolute',
        color: 'text.disabled',
        bgcolor: (theme) => alpha(theme.palette.grey[500], 0.08),
        transition: (theme) =>
          theme.transitions.create(['opacity'], {
            duration: theme.transitions.duration.shorter,
          }),
        '&:hover': {
          opacity: 0.72,
        },
        ...(hasError && {
          color: 'error.main',
          bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
        }),
        ...(hasFile && {
          zIndex: 9,
          opacity: 0,
          color: 'common.white',
          bgcolor: (theme) => alpha(theme.palette.grey[900], 0.64),
        }),
      }}
    >
      <Iconify icon="solar:camera-add-bold" width={32} />

      <Typography variant="caption">{file ? 'Update photo' : 'Upload photo'}</Typography>
    </Stack>
  );

  const renderContent = (
    <Box
      sx={{
        width: 1,
        height: 1,
        overflow: 'hidden',
        borderRadius: circularCrop ? '50%' : 1,
        position: 'relative',
      }}
    >
      {renderPreview}
      {renderPlaceholder}
    </Box>
  );

  return (
    <>
      <Box
        {...getRootProps()}
        sx={{
          p: 1,
          m: 'auto',
          width: 144,
          height: 144,
          cursor: 'pointer',
          overflow: 'hidden',
          borderRadius: circularCrop ? '50%' : 1,
          border: (theme) => `1px dashed ${alpha(theme.palette.grey[500], 0.2)}`,
          ...(isDragActive && {
            opacity: 0.72,
          }),
          ...(disabled && {
            opacity: 0.48,
            pointerEvents: 'none',
          }),
          ...(hasError && {
            borderColor: 'error.main',
          }),
          ...(hasFile && {
            ...(hasError && {
              bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
            }),
            '&:hover .upload-placeholder': {
              opacity: 1,
            },
          }),
          ...sx,
        }}
      >
        <input {...getInputProps()} />

        {renderContent}
      </Box>

      {helperText && helperText}

      <RejectionFiles fileRejections={fileRejections} />

      {crop && (
        <ImageCropperDialog
          open={openCropper}
          image={cropImage}
          onClose={() => setOpenCropper(false)}
          onSave={handleSaveCroppedImage}
          aspectRatio={aspectRatio}
          circularCrop={circularCrop}
          lockAspectRatio={lockAspectRatio}
        />
      )}
    </>
  );
}

UploadAvatar.propTypes = {
  aspectRatio: PropTypes.number,
  circularCrop: PropTypes.bool,
  lockAspectRatio: PropTypes.bool,
  crop: PropTypes.bool,
  disabled: PropTypes.object,
  error: PropTypes.bool,
  file: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  helperText: PropTypes.object,
  onDrop: PropTypes.func,
  sx: PropTypes.object,
};
